import { useEffect } from 'react';
import { history } from 'src/utils/history';

const COUNTER_ID = 112761520;
const LEGACY_CONSENT_COOKIE = 'lm_analytics_consent';
const SHARED_CONSENT_COOKIE = 'lm_analytics_consent_v2';
const SHARED_CONSENT_PREFIX = 'v2.';
const CONSENT_CHOICE_KEY = 'lm_analytics_consent_choice';
const CONSENT_MIGRATED_KEY = 'lm_analytics_consent_shared_v2_migrated';
const CAPTURE_STATE_KEY = 'lm_acquisition_app_capture_v1';
const SESSION_KEY = 'lm_acquisition_app_session_v1';
const HANDOFF_FRAGMENT_KEY = 'lm_handoff';
const PURCHASE_CONSENT_EVENT_KEY = 'lm_purchase_consent_event_v1';
const MAX_CAPABILITY_LENGTH = 2048;
const CAPABILITY_TTL_MS = 300_000;
const REFRESH_MARGIN_MS = 60_000;
const REFRESH_RETRY_MS = 10_000;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30_000;
const MAX_TIMER_MS = 2_147_000_000;
const OBSERVATION_TOUCH_WAIT_MS = 250;
const PURCHASE_IDENTITY_RETRY_MS = [1000, 5000, 30_000];
export const ANALYTICS_CONSENT_CHANGE_EVENT = 'lm-analytics-consent-change';
const REGISTRATION_SUCCESS_GOAL = 'registration_success';

type ConsentChoice = 'accepted' | 'rejected' | 'unknown';
type TouchSnapshot = {
    eventId: string;
    key: string;
    client_id?: string;
    yclid?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
};
type BrowserObservation = {
    eventId: string;
    eventType: 'registration_started';
    surface: 'registration';
    key: string;
};
type PurchaseConsentRecord = {
    decision: 'accepted' | 'rejected';
    eventId: string;
    previousEventId: string;
};
type PurchaseConsentState = {
    acknowledged: PurchaseConsentRecord | null;
    pending: PurchaseConsentRecord | null;
};
type CaptureState = {
    capability: string;
    expiresAt: number;
    serverGranted: boolean;
    acceptEventId: string;
    withdrawEventId: string;
    pendingTouch: TouchSnapshot | null;
    lastTouchKey: string;
    rotateAfterWithdraw: boolean;
    boundCapability: string;
    pendingObservations: BrowserObservation[];
    deliveredObservationKeys: string[];
};

type RequestResult =
    | { kind: 'success'; data: unknown }
    | { kind: 'permanent'; status: number }
    | { kind: 'retryable'; status: number; retryAfterMs: number };

export type AcquisitionBindTransportResult = {
    status: number;
    data?: unknown;
    retryAfterMs?: number;
};

export type AcquisitionBindTransport = (capability: string) => Promise<AcquisitionBindTransportResult>;
export type AcquisitionSessionResolution = 'pending' | 'anonymous' | 'authenticated';
export type PurchaseIdentityPayload = {
    event_id: string;
    previous_event_id?: string;
    decision: 'accepted' | 'rejected';
    account_id: string;
    client_id?: string;
    yclid?: string;
};
export type PurchaseIdentityTransport = (payload: PurchaseIdentityPayload) => Promise<AcquisitionBindTransportResult>;

type Ym = ((counter: number, method: string, ...args: unknown[]) => void) & {
    __lmStub?: boolean;
    a?: unknown[][];
    l?: number;
};

declare global {
    interface Window {
        ym?: Ym;
        disableYaCounter112761520?: boolean;
    }
}

const emptyState = (): CaptureState => ({
    capability: '',
    expiresAt: 0,
    serverGranted: false,
    acceptEventId: '',
    withdrawEventId: '',
    pendingTouch: null,
    lastTouchKey: '',
    rotateAfterWithdraw: false,
    boundCapability: '',
    pendingObservations: [],
    deliveredObservationKeys: [],
});

let vendorLoaded = false;
let vendorInitialized = false;
let lifecycleGeneration = 0;
let observedConsent: ConsentChoice = 'unknown';
let refreshTimer = 0;
let lifecycleRetryTimer = 0;
let lifecycleRetryAt = 0;
let syncTimer = 0;
let lastHitPath = '';
let started = false;
let subjectAttemptGeneration = -1;
let captureState: CaptureState = emptyState();
let lifecycleChain: Promise<void> = Promise.resolve();
let authenticatedSessionResolution: AcquisitionSessionResolution = 'pending';
let authenticatedSessionIdentity = '';
let authenticatedSessionGeneration = 0;
let authenticatedBindTransport: AcquisitionBindTransport | null = null;
let purchaseIdentityTransport: PurchaseIdentityTransport | null = null;
let trustedBoundCapability = '';
let bindRequiredBeforeTouch = false;
let touchReadyCapability = '';
let registrationSuccessGoalPending = false;
let registrationSuccessGoalDelivered = false;
let purchaseIdentityGeneration = 0;
let purchaseIdentityRetryAttempt = 0;
let purchaseIdentityRetryTimer = 0;
let purchaseIdentityFingerprint = '';
let purchaseIdentityChain: Promise<void> = Promise.resolve();
const permanentOperations = new Set<string>();
const retryOperations = new Map<string, { attempt: number; nextAt: number }>();

function canonicalUuid() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const value = Math.floor(Math.random() * 16);
        return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    });
}

function validUuid(value: unknown): value is string {
    return (
        typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value)
    );
}

function capability(value: unknown) {
    return typeof value === 'string' &&
        value.length > 0 &&
        value.length <= MAX_CAPABILITY_LENGTH &&
        /^[\x21-\x7e]+$/.test(value)
        ? value
        : '';
}

function safeAttribution(value: unknown) {
    const text = typeof value === 'string' ? value.slice(0, 120) : '';
    return text && /^[a-zA-Z0-9._~/-]+$/.test(text) ? text : '';
}

function validClientId(value: unknown) {
    return typeof value === 'string' && /^[0-9]{1,32}(?:\.[0-9]{1,32})?$/.test(value) ? value : '';
}

function validPurchaseClientId(value: unknown) {
    return typeof value === 'string' && /^[0-9]{5,32}$/.test(value) ? value : '';
}

function validYclid(value: unknown) {
    return typeof value === 'string' && /^[A-Za-z0-9_-]{8,255}$/.test(value) ? value : '';
}

function cookieValues(name: string) {
    return document.cookie
        .split(';')
        .map((item) => item.trim())
        .filter((item) => item.startsWith(`${name}=`))
        .map((item) => item.slice(name.length + 1));
}

function sharedCookieChoice(): ConsentChoice {
    const values = cookieValues(SHARED_CONSENT_COOKIE);
    if (values.includes(`${SHARED_CONSENT_PREFIX}rejected`)) return 'rejected';
    if (values.includes(`${SHARED_CONSENT_PREFIX}accepted`)) return 'accepted';
    return 'unknown';
}

function legacyCookieChoice(): ConsentChoice {
    const values = cookieValues(LEGACY_CONSENT_COOKIE);
    if (values.includes('rejected')) return 'rejected';
    if (values.includes('accepted')) return 'accepted';
    return 'unknown';
}

function persistSharedChoice(choice: Exclude<ConsentChoice, 'unknown'>) {
    try {
        window.localStorage.setItem(CONSENT_CHOICE_KEY, choice);
        window.localStorage.setItem(CONSENT_MIGRATED_KEY, '1');
    } catch {}
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        const ownedHost =
            window.location.hostname === 'leadsmania.ru' || window.location.hostname.endsWith('.leadsmania.ru');
        document.cookie = `${LEGACY_CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
        document.cookie = `${SHARED_CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
        if (ownedHost) {
            document.cookie = `${LEGACY_CONSENT_COOKIE}=; Max-Age=0; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
            document.cookie = `${SHARED_CONSENT_COOKIE}=; Max-Age=0; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
            document.cookie = `${SHARED_CONSENT_COOKIE}=${SHARED_CONSENT_PREFIX}${choice}; Max-Age=31536000; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
        } else {
            document.cookie = `${SHARED_CONSENT_COOKIE}=${SHARED_CONSENT_PREFIX}${choice}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
        }
    } catch {}
}

export function setAnalyticsConsentChoice(choice: Exclude<ConsentChoice, 'unknown'>) {
    persistSharedChoice(choice);
    window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGE_EVENT));
    window.queueMicrotask(synchronizePurchaseIdentity);
}

export function migrateAnalyticsConsent(): ConsentChoice {
    let migrated = false;
    let legacy = '';
    try {
        migrated = window.localStorage.getItem(CONSENT_MIGRATED_KEY) === '1';
        legacy = window.localStorage.getItem(CONSENT_CHOICE_KEY) || '';
    } catch {}
    const shared = sharedCookieChoice();
    if (shared !== 'unknown') {
        try {
            window.localStorage.setItem(CONSENT_MIGRATED_KEY, '1');
        } catch {}
        return shared;
    }
    if (!migrated) {
        const legacyCookie = legacyCookieChoice();
        const selected =
            legacyCookie !== 'unknown'
                ? legacyCookie
                : legacy === 'accepted' || legacy === 'rejected'
                ? legacy
                : 'unknown';
        if (selected !== 'unknown') persistSharedChoice(selected);
        try {
            window.localStorage.setItem(CONSENT_MIGRATED_KEY, '1');
        } catch {}
        return selected;
    }
    return 'unknown';
}

export function analyticsConsentChoice(): ConsentChoice {
    return sharedCookieChoice();
}

function validPurchaseConsentRecord(value: unknown): PurchaseConsentRecord | null {
    if (!value || typeof value !== 'object') return null;
    const record = value as Record<string, unknown>;
    if (
        (record.decision !== 'accepted' && record.decision !== 'rejected') ||
        !validUuid(record.eventId) ||
        (record.previousEventId !== '' && !validUuid(record.previousEventId))
    )
        return null;
    return {
        decision: record.decision,
        eventId: record.eventId,
        previousEventId: record.previousEventId,
    };
}

function readPurchaseConsentState(accountId: string): PurchaseConsentState {
    try {
        const key = `${PURCHASE_CONSENT_EVENT_KEY}:${accountId}`;
        const existing = JSON.parse(window.localStorage.getItem(key) || '{}') as Record<string, unknown>;
        const acknowledged = validPurchaseConsentRecord(existing.acknowledged);
        const pending = validPurchaseConsentRecord(existing.pending);
        if (acknowledged || pending) return { acknowledged, pending };
        const legacy = validPurchaseConsentRecord(existing);
        return { acknowledged: legacy, pending: null };
    } catch {
        return { acknowledged: null, pending: null };
    }
}

function persistPurchaseConsentState(accountId: string, state: PurchaseConsentState) {
    try {
        window.localStorage.setItem(`${PURCHASE_CONSENT_EVENT_KEY}:${accountId}`, JSON.stringify(state));
        return true;
    } catch {
        return false;
    }
}

function purchaseConsentEvent(accountId: string, decision: 'accepted' | 'rejected') {
    const state = readPurchaseConsentState(accountId);
    if (state.pending?.decision === decision) return state.pending;
    if (!state.pending && state.acknowledged?.decision === decision) return state.acknowledged;
    const event: PurchaseConsentRecord = {
        decision,
        eventId: canonicalUuid(),
        previousEventId: decision === 'accepted' ? state.acknowledged?.eventId || '' : '',
    };
    if (!persistPurchaseConsentState(accountId, { ...state, pending: event })) {
        return { decision, eventId: '', previousEventId: '' };
    }
    return event;
}

function acknowledgePurchaseConsent(accountId: string, event: PurchaseConsentRecord) {
    const state = readPurchaseConsentState(accountId);
    if (state.pending?.eventId !== event.eventId) return;
    persistPurchaseConsentState(accountId, { acknowledged: event, pending: null });
}

function clearPurchaseIdentityRetry() {
    if (purchaseIdentityRetryTimer) window.clearTimeout(purchaseIdentityRetryTimer);
    purchaseIdentityRetryTimer = 0;
}

function schedulePurchaseIdentityRetry(expectedGeneration: number) {
    if (purchaseIdentityRetryAttempt >= PURCHASE_IDENTITY_RETRY_MS.length) return;
    const delay = PURCHASE_IDENTITY_RETRY_MS[purchaseIdentityRetryAttempt++];
    clearPurchaseIdentityRetry();
    purchaseIdentityRetryTimer = window.setTimeout(() => {
        purchaseIdentityRetryTimer = 0;
        if (expectedGeneration === purchaseIdentityGeneration) synchronizePurchaseIdentity();
    }, delay);
}

async function postPurchaseIdentityNow(
    decision: 'accepted' | 'rejected',
    clientId = '',
    yclid = '',
    expectedGeneration = purchaseIdentityGeneration
) {
    const transport = purchaseIdentityTransport;
    const accountId = authenticatedSessionIdentity;
    if (
        !transport ||
        authenticatedSessionResolution !== 'authenticated' ||
        !/^[1-9][0-9]*$/.test(accountId) ||
        expectedGeneration !== purchaseIdentityGeneration ||
        analyticsConsentChoice() !== decision ||
        (decision === 'accepted' && !clientId && !yclid)
    )
        return;
    const event = purchaseConsentEvent(accountId, decision);
    const { eventId, previousEventId } = event;
    if (!eventId) return;
    const body: PurchaseIdentityPayload = {
        event_id: eventId,
        ...(previousEventId ? { previous_event_id: previousEventId } : {}),
        decision,
        account_id: accountId,
        ...(decision === 'accepted' && clientId ? { client_id: clientId } : {}),
        ...(decision === 'accepted' && yclid ? { yclid } : {}),
    };
    const fingerprint = JSON.stringify(body);
    if (fingerprint === purchaseIdentityFingerprint) return;
    purchaseIdentityFingerprint = fingerprint;
    let result: AcquisitionBindTransportResult;
    try {
        result = await transport(body);
    } catch {
        result = { status: 0 };
    }
    if (expectedGeneration !== purchaseIdentityGeneration) return;
    const data = result.data as Record<string, unknown> | undefined;
    const expectedState = decision === 'accepted' ? 'granted' : 'denied';
    if (
        result.status === 202 &&
        data?.recorded === true &&
        typeof data.created === 'boolean' &&
        data.state === expectedState
    ) {
        purchaseIdentityRetryAttempt = 0;
        clearPurchaseIdentityRetry();
        acknowledgePurchaseConsent(accountId, event);
        return;
    }
    if (classifyStatus(result.status) === 'retryable') {
        if (purchaseIdentityFingerprint === fingerprint) purchaseIdentityFingerprint = '';
        schedulePurchaseIdentityRetry(expectedGeneration);
    }
}

function postPurchaseIdentity(
    decision: 'accepted' | 'rejected',
    clientId = '',
    yclid = '',
    expectedGeneration = purchaseIdentityGeneration
) {
    const execute = () => postPurchaseIdentityNow(decision, clientId, yclid, expectedGeneration);
    purchaseIdentityChain = purchaseIdentityChain.then(execute, execute).catch(() => {});
}

export function synchronizePurchaseIdentity() {
    if (
        !purchaseIdentityTransport ||
        authenticatedSessionResolution !== 'authenticated' ||
        !/^[1-9][0-9]*$/.test(authenticatedSessionIdentity)
    )
        return;
    const expectedGeneration = purchaseIdentityGeneration;
    const choice = analyticsConsentChoice();
    if (choice === 'rejected') {
        void postPurchaseIdentity('rejected', '', '', expectedGeneration);
        return;
    }
    if (choice !== 'accepted') return;
    const yclid = validYclid(new URLSearchParams(window.location.search).get('yclid'));
    if (!vendorInitialized || typeof window.ym !== 'function') {
        if (yclid) void postPurchaseIdentity('accepted', '', yclid, expectedGeneration);
        return;
    }
    const generation = lifecycleGeneration;
    void metrikaClientId(generation).then((clientId) => {
        if (
            expectedGeneration !== purchaseIdentityGeneration ||
            authenticatedSessionResolution !== 'authenticated' ||
            analyticsConsentChoice() !== 'accepted'
        )
            return;
        const purchaseClientId = validPurchaseClientId(clientId);
        if (purchaseClientId || yclid)
            void postPurchaseIdentity('accepted', purchaseClientId, yclid, expectedGeneration);
    });
}

export function configurePurchaseIdentityTransport(transport: PurchaseIdentityTransport | null) {
    purchaseIdentityTransport = transport;
    purchaseIdentityFingerprint = '';
    purchaseIdentityRetryAttempt = 0;
    clearPurchaseIdentityRetry();
    if (transport) synchronizePurchaseIdentity();
}

function validTouch(value: unknown): TouchSnapshot | null {
    if (!value || typeof value !== 'object') return null;
    const candidate = value as Record<string, unknown>;
    if (!validUuid(candidate.eventId) || typeof candidate.key !== 'string' || candidate.key.length > 500) return null;
    const result: TouchSnapshot = { eventId: candidate.eventId, key: candidate.key };
    const clientId = validClientId(candidate.client_id);
    const yclid = validYclid(candidate.yclid);
    if (clientId) result.client_id = clientId;
    if (yclid) result.yclid = yclid;
    const utmFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
    utmFields.forEach((field) => {
        const safe = safeAttribution(candidate[field]);
        if (safe) result[field] = safe;
    });
    return result.client_id || result.yclid || result.utm_source ? result : null;
}

function validObservation(value: unknown): BrowserObservation | null {
    if (!value || typeof value !== 'object') return null;
    const candidate = value as Record<string, unknown>;
    if (
        !validUuid(candidate.eventId) ||
        candidate.eventType !== 'registration_started' ||
        candidate.surface !== 'registration' ||
        candidate.key !== 'registration-started'
    )
        return null;
    return {
        eventId: candidate.eventId,
        eventType: 'registration_started',
        surface: 'registration',
        key: 'registration-started',
    };
}

function readCaptureState(): CaptureState {
    try {
        const raw = JSON.parse(window.localStorage.getItem(CAPTURE_STATE_KEY) || 'null');
        if (!raw || typeof raw !== 'object') return emptyState();
        return {
            capability: capability(raw.capability),
            expiresAt: Number.isFinite(raw.expiresAt) && raw.expiresAt > 0 ? raw.expiresAt : 0,
            serverGranted: raw.serverGranted === true,
            acceptEventId: validUuid(raw.acceptEventId) ? raw.acceptEventId : '',
            withdrawEventId: validUuid(raw.withdrawEventId) ? raw.withdrawEventId : '',
            pendingTouch: validTouch(raw.pendingTouch),
            lastTouchKey:
                typeof raw.lastTouchKey === 'string' && raw.lastTouchKey.length <= 500 ? raw.lastTouchKey : '',
            rotateAfterWithdraw: raw.rotateAfterWithdraw === true,
            boundCapability: capability(raw.boundCapability),
            pendingObservations: Array.isArray(raw.pendingObservations)
                ? raw.pendingObservations.map(validObservation).filter(Boolean).slice(0, 8)
                : [],
            deliveredObservationKeys: Array.isArray(raw.deliveredObservationKeys)
                ? raw.deliveredObservationKeys.filter((value: unknown) => value === 'registration-started').slice(0, 8)
                : [],
        };
    } catch {
        return emptyState();
    }
}

function persistCaptureState() {
    try {
        window.localStorage.setItem(CAPTURE_STATE_KEY, JSON.stringify(captureState));
    } catch {}
}

export function captureAcquisitionHandoffFromFragment(now = Date.now()) {
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
    const params = new URLSearchParams(hash);
    const allowedOrigin =
        window.location.origin === 'https://app.leadsmania.ru' ||
        ['127.0.0.1', 'localhost'].includes(window.location.hostname);
    const token = allowedOrigin ? capability(params.get(HANDOFF_FRAGMENT_KEY)) : '';
    if (params.has(HANDOFF_FRAGMENT_KEY)) {
        params.delete(HANDOFF_FRAGMENT_KEY);
        const remaining = params.toString();
        window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`
        );
    }
    captureState = readCaptureState();
    const pendingOldWithdrawal = Boolean(captureState.withdrawEventId);
    const canAdopt = Boolean(
        token && !(captureState.rotateAfterWithdraw && captureState.capability) && !pendingOldWithdrawal
    );
    if (canAdopt) {
        const changedSubject = token !== captureState.capability;
        captureState = !changedSubject
            ? { ...captureState, expiresAt: now + CAPABILITY_TTL_MS }
            : { ...emptyState(), capability: token, expiresAt: now + CAPABILITY_TTL_MS };
        if (changedSubject) {
            trustedBoundCapability = '';
            bindRequiredBeforeTouch = false;
            touchReadyCapability = '';
            resetOperationGuards();
        }
        persistCaptureState();
    }
    return canAdopt ? token : '';
}

function currentCapability(now = Date.now(), skewMs = 0) {
    return captureState.capability && now + skewMs < captureState.expiresAt ? captureState.capability : '';
}

export function acquisitionHandoffForAuth(now = Date.now()) {
    captureState = readCaptureState();
    if (analyticsConsentChoice() !== 'accepted') return '';
    if (captureState.rotateAfterWithdraw) return '';
    return currentCapability(now);
}

export function withAcquisitionHandoff<T extends object>(payload: T): T & { acquisition_handoff_token?: string } {
    const token = acquisitionHandoffForAuth();
    return token ? { ...payload, acquisition_handoff_token: token } : payload;
}

function apiBase() {
    if (process.env.REACT_APP_ACQUISITION_CAPTURE_ENABLED !== 'true') return null;
    try {
        const value = new URL(
            process.env.REACT_APP_ACQUISITION_API_BASE || '/api/acquisition/',
            window.location.origin
        );
        const exactProduction =
            window.location.origin === 'https://app.leadsmania.ru' && value.origin === 'https://app.leadsmania.ru';
        const local =
            ['127.0.0.1', 'localhost'].includes(window.location.hostname) && value.origin === window.location.origin;
        if ((!exactProduction && !local) || !value.pathname.endsWith('/api/acquisition/')) return null;
        value.search = '';
        value.hash = '';
        return value;
    } catch {
        return null;
    }
}

function retryAfterMs(response: Response) {
    const value = response.headers?.get?.('Retry-After') || '';
    if (!value) return 0;
    const seconds = Number(value);
    const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now();
    return Number.isFinite(delay) ? Math.max(0, delay) : 0;
}

function classifyStatus(status: number) {
    if (status === 429 || status >= 500 || status === 0) return 'retryable' as const;
    return 'permanent' as const;
}

function operationKey(path: string, body: Record<string, unknown>) {
    return `${path}:${JSON.stringify(body)}`;
}

function clearLifecycleRetryTimer() {
    if (lifecycleRetryTimer) window.clearTimeout(lifecycleRetryTimer);
    lifecycleRetryTimer = 0;
    lifecycleRetryAt = 0;
}

function resetOperationGuards(prefix?: string) {
    if (!prefix) {
        permanentOperations.clear();
        retryOperations.clear();
        clearLifecycleRetryTimer();
        return;
    }
    Array.from(permanentOperations).forEach((key) => {
        if (key.startsWith(prefix)) permanentOperations.delete(key);
    });
    Array.from(retryOperations.keys()).forEach((key) => {
        if (key.startsWith(prefix)) retryOperations.delete(key);
    });
}

function scheduleLifecycleRetry(delay: number) {
    const normalizedDelay = Math.min(MAX_TIMER_MS, Math.max(RETRY_BASE_MS, delay));
    const target = Date.now() + normalizedDelay;
    if (lifecycleRetryTimer && lifecycleRetryAt <= target) return;
    clearLifecycleRetryTimer();
    lifecycleRetryAt = target;
    lifecycleRetryTimer = window.setTimeout(() => {
        lifecycleRetryTimer = 0;
        lifecycleRetryAt = 0;
        captureState = readCaptureState();
        const generation = lifecycleGeneration;
        queueLifecycle(async () => {
            if (analyticsConsentChoice() === 'accepted') await ensureAcceptedLifecycle(generation, true);
            else await ensureRejectedLifecycle();
        });
    }, normalizedDelay);
}

function registerRetry(key: string, requestedDelay = 0) {
    const previous = retryOperations.get(key);
    const attempt = Math.min((previous?.attempt || 0) + 1, 8);
    const exponential = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (attempt - 1));
    const delay = Math.max(exponential, requestedDelay);
    retryOperations.set(key, { attempt, nextAt: Date.now() + delay });
    scheduleLifecycleRetry(delay);
    return delay;
}

function capabilityOperationKey(key: string, previousToken: string, nextToken: string) {
    const bindKey = `bind:${authenticatedSessionGeneration}:${previousToken}`;
    if (key === bindKey) return `bind:${authenticatedSessionGeneration}:${nextToken}`;
    const separator = key.indexOf(':');
    if (separator < 0) return '';
    try {
        const body = JSON.parse(key.slice(separator + 1)) as Record<string, unknown>;
        if (body.capability !== previousToken) return '';
        body.capability = nextToken;
        return `${key.slice(0, separator)}:${JSON.stringify(body)}`;
    } catch {
        return '';
    }
}

function carryCapabilityOperationGuards(previousToken: string, nextToken: string) {
    Array.from(retryOperations.entries()).forEach(([key, retry]) => {
        const nextKey = capabilityOperationKey(key, previousToken, nextToken);
        if (!nextKey) return;
        retryOperations.delete(key);
        retryOperations.set(nextKey, retry);
    });
    Array.from(permanentOperations).forEach((key) => {
        const nextKey = capabilityOperationKey(key, previousToken, nextToken);
        if (!nextKey) return;
        permanentOperations.delete(key);
        permanentOperations.add(nextKey);
    });
}

async function postOnce(path: string, body: Record<string, unknown>, expectedStatus: number): Promise<RequestResult> {
    const base = apiBase();
    if (!base) return { kind: 'permanent', status: 0 };
    try {
        const response = await window.fetch(new URL(path, base).toString(), {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (response.status !== expectedStatus) {
            const kind = classifyStatus(response.status);
            return kind === 'retryable'
                ? { kind, status: response.status, retryAfterMs: retryAfterMs(response) }
                : { kind, status: response.status };
        }
        try {
            return { kind: 'success', data: await response.json() };
        } catch {
            return { kind: 'permanent', status: response.status };
        }
    } catch {
        return { kind: 'retryable', status: 0, retryAfterMs: 0 };
    }
}

async function postStable(
    path: string,
    body: Record<string, unknown>,
    expectedStatus: number,
    _attempts = 1,
    shouldContinue: () => boolean = () => true
) {
    const key = operationKey(path, body);
    if (!shouldContinue() || permanentOperations.has(key)) return null;
    const retry = retryOperations.get(key);
    if (retry && retry.nextAt > Date.now()) {
        scheduleLifecycleRetry(retry.nextAt - Date.now());
        return null;
    }
    const result = await postOnce(path, body, expectedStatus);
    if (!shouldContinue()) return null;
    if (result.kind === 'success') {
        retryOperations.delete(key);
        permanentOperations.delete(key);
        return result.data;
    }
    if (result.kind === 'permanent') {
        retryOperations.delete(key);
        permanentOperations.add(key);
        return null;
    }
    registerRetry(key, result.retryAfterMs);
    return null;
}

function validCapabilityResponse(value: unknown, tokenField: 'capability' | 'handoff_token') {
    if (!value || typeof value !== 'object') return null;
    const response = value as Record<string, unknown>;
    const token = capability(response[tokenField]);
    const expiresIn = response.expires_in;
    if (!token || !Number.isInteger(expiresIn) || Number(expiresIn) <= 0 || Number(expiresIn) > 300) return null;
    return { token, expiresIn: Number(expiresIn) };
}

async function createSubject(generation: number) {
    if (subjectAttemptGeneration === generation) return '';
    subjectAttemptGeneration = generation;
    const result = await postOnce('subject/', {}, 201);
    const response = result.kind === 'success' ? result.data : null;
    const parsed = validCapabilityResponse(response, 'capability');
    if (!parsed || (response as Record<string, unknown>)?.counter_id !== COUNTER_ID) return '';
    const rotateAfterWithdraw = captureState.rotateAfterWithdraw;
    const pendingObservations =
        generation === lifecycleGeneration && analyticsConsentChoice() === 'accepted'
            ? [...captureState.pendingObservations]
            : [];
    resetOperationGuards();
    trustedBoundCapability = '';
    bindRequiredBeforeTouch = false;
    touchReadyCapability = '';
    captureState = {
        ...emptyState(),
        capability: parsed.token,
        expiresAt: Date.now() + parsed.expiresIn * 1000,
        rotateAfterWithdraw,
        pendingObservations,
    };
    persistCaptureState();
    return parsed.token;
}

async function recordConsent(decision: 'accept' | 'withdraw', force = false, generation?: number) {
    if (!captureState.capability) return false;
    if (
        decision === 'withdraw' &&
        !force &&
        !captureState.serverGranted &&
        !captureState.acceptEventId &&
        !captureState.withdrawEventId
    )
        return true;
    const eventKey = decision === 'accept' ? 'acceptEventId' : 'withdrawEventId';
    if (!captureState[eventKey]) {
        captureState[eventKey] = canonicalUuid();
        persistCaptureState();
    }
    const response = await postStable(
        'consent/',
        { capability: captureState.capability, event_id: captureState[eventKey], decision },
        202,
        3,
        decision === 'withdraw'
            ? () => true
            : () => generation === lifecycleGeneration && analyticsConsentChoice() === 'accepted'
    );
    const expected = decision === 'accept' ? 'granted' : 'denied';
    const data = response as Record<string, unknown> | null;
    if (!data || data.state !== expected || typeof data.created !== 'boolean') return false;
    captureState.serverGranted = decision === 'accept';
    captureState[eventKey] = '';
    if (decision === 'withdraw') {
        captureState.acceptEventId = '';
        captureState.pendingTouch = null;
        captureState.lastTouchKey = '';
        captureState.pendingObservations = [];
        captureState.deliveredObservationKeys = [];
        touchReadyCapability = '';
    }
    persistCaptureState();
    return true;
}

function appSessionId() {
    try {
        const existing = window.sessionStorage.getItem(SESSION_KEY);
        if (validUuid(existing)) return existing;
        const created = canonicalUuid();
        window.sessionStorage.setItem(SESSION_KEY, created);
        return created;
    } catch {
        return canonicalUuid();
    }
}

function currentTouchKey() {
    const query = new URLSearchParams(window.location.search);
    const allowed = new URLSearchParams();
    ['yclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((field) => {
        const value = field === 'yclid' ? validYclid(query.get(field)) : safeAttribution(query.get(field));
        if (value) allowed.set(field, value);
    });
    return `${appSessionId()}:${window.location.pathname || '/'}?${allowed.toString()}`;
}

function attributionFromLocation() {
    const query = new URLSearchParams(window.location.search);
    const result: Partial<TouchSnapshot> = {};
    const yclid = validYclid(query.get('yclid'));
    if (yclid) result.yclid = yclid;
    const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
    fields.forEach((field) => {
        const value = safeAttribution(query.get(field));
        if (value) result[field] = value;
    });
    return result;
}

async function metrikaClientId(generation: number) {
    for (let attempt = 0; attempt < 40; attempt += 1) {
        if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return '';
        if (vendorInitialized && typeof window.ym === 'function') {
            const value = await new Promise<string>((resolve) => {
                let completed = false;
                let timeout = 0;
                const finish = (clientId: unknown = '') => {
                    if (completed) return;
                    completed = true;
                    if (timeout) window.clearTimeout(timeout);
                    resolve(typeof clientId === 'string' ? clientId : '');
                };
                try {
                    window.ym?.(COUNTER_ID, 'getClientID', finish);
                } catch {
                    finish('');
                }
                timeout = window.setTimeout(() => finish(''), 100);
            });
            return validClientId(value);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 25));
    }
    return '';
}

function attributionTouchBody(snapshot: TouchSnapshot) {
    const body: Record<string, string> = {};
    const fields = [
        'client_id',
        'yclid',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
    ] as const;
    fields.forEach((field) => {
        if (snapshot[field]) body[field] = snapshot[field] as string;
    });
    return body;
}

async function recordTouch(generation: number) {
    if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted' || !currentCapability())
        return false;
    const key = currentTouchKey();
    if (!captureState.pendingTouch && captureState.lastTouchKey === key) {
        touchReadyCapability = captureState.capability;
        return true;
    }
    if (!captureState.pendingTouch) {
        const clientId = await metrikaClientId(generation);
        if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return false;
        const identifiers = attributionFromLocation();
        if (!clientId && !identifiers.yclid && !identifiers.utm_source) return false;
        captureState.pendingTouch = {
            eventId: canonicalUuid(),
            key,
            ...(clientId ? { client_id: clientId } : {}),
            ...identifiers,
        };
        persistCaptureState();
    }
    const pending = captureState.pendingTouch;
    const response = await postStable(
        'touch/',
        { capability: captureState.capability, event_id: pending.eventId, ...attributionTouchBody(pending) },
        202,
        3,
        () => generation === lifecycleGeneration && analyticsConsentChoice() === 'accepted'
    );
    const data = response as Record<string, unknown> | null;
    if (!data || typeof data.created !== 'boolean') return false;
    captureState.lastTouchKey = pending.key;
    captureState.pendingTouch = null;
    touchReadyCapability = captureState.capability;
    persistCaptureState();
    return true;
}

async function flushPendingObservation(generation: number, pending: BrowserObservation, capabilityAtStart: string) {
    const payload = {
        capability: capabilityAtStart,
        event_id: pending.eventId,
        event_type: pending.eventType,
        surface: pending.surface,
    };
    const response = await postStable(
        'observation/',
        payload,
        202,
        1,
        () =>
            generation === lifecycleGeneration &&
            analyticsConsentChoice() === 'accepted' &&
            capabilityAtStart === captureState.capability
    );
    const data = response as Record<string, unknown> | null;
    if (!data || typeof data.created !== 'boolean') {
        if (permanentOperations.has(operationKey('observation/', payload))) {
            if (captureState.pendingObservations[0]?.eventId !== pending.eventId) return false;
            captureState.pendingObservations.shift();
            persistCaptureState();
            return true;
        }
        return false;
    }
    if (captureState.pendingObservations[0]?.eventId !== pending.eventId) return false;
    captureState.pendingObservations.shift();
    if (!captureState.deliveredObservationKeys.includes(pending.key)) {
        captureState.deliveredObservationKeys.push(pending.key);
        captureState.deliveredObservationKeys = captureState.deliveredObservationKeys.slice(-8);
    }
    persistCaptureState();
    return true;
}

async function flushPendingObservations(generation: number) {
    if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted' || !captureState.serverGranted)
        return false;
    while (captureState.pendingObservations.length) {
        if (
            !(await flushPendingObservation(generation, captureState.pendingObservations[0], captureState.capability))
        ) {
            return false;
        }
    }
    return true;
}

async function attemptTouchBeforeObservations(generation: number, requestTouch: boolean) {
    if (requestTouch) {
        let timeout = 0;
        await Promise.race([
            recordTouch(generation),
            new Promise<boolean>((resolve) => {
                timeout = window.setTimeout(() => resolve(false), OBSERVATION_TOUCH_WAIT_MS);
            }),
        ]);
        if (timeout) window.clearTimeout(timeout);
    }
    await flushPendingObservations(generation);
}

export function recordRegistrationStartedObservation() {
    if (analyticsConsentChoice() !== 'accepted') return false;
    captureState = readCaptureState();
    const key = 'registration-started';
    if (
        captureState.deliveredObservationKeys.includes(key) ||
        captureState.pendingObservations.some((observation) => observation.key === key)
    )
        return true;
    if (captureState.pendingObservations.length >= 8) return false;
    captureState.pendingObservations.push({
        eventId: canonicalUuid(),
        eventType: 'registration_started',
        surface: 'registration',
        key,
    });
    persistCaptureState();
    if (started) {
        const generation = lifecycleGeneration;
        queueLifecycle(() => ensureAcceptedLifecycle(generation, true));
    }
    return true;
}

/**
 * Emit the registration goal only after the registration API has confirmed
 * account creation. The counter is unavailable until analytics consent is
 * granted, so rejected or undecided visitors remain untracked.
 */
export function recordRegistrationSuccessGoal() {
    if (analyticsConsentChoice() !== 'accepted') return false;
    if (registrationSuccessGoalDelivered || registrationSuccessGoalPending) return true;
    if (!vendorInitialized || !window.ym) {
        registrationSuccessGoalPending = true;
        if (started) loadVendor(lifecycleGeneration);
        return true;
    }
    window.ym(COUNTER_ID, 'reachGoal', REGISTRATION_SUCCESS_GOAL);
    registrationSuccessGoalDelivered = true;
    return true;
}

function clearRefreshTimer() {
    if (refreshTimer) window.clearTimeout(refreshTimer);
    refreshTimer = 0;
}

function scheduleRefresh(generation: number, retry = false) {
    clearRefreshTimer();
    if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted' || !captureState.capability)
        return;
    const remaining = captureState.expiresAt - Date.now();
    const delay = retry
        ? Math.min(REFRESH_RETRY_MS, Math.max(1000, remaining))
        : Math.max(1000, remaining - REFRESH_MARGIN_MS);
    refreshTimer = window.setTimeout(() => {
        queueLifecycle(async () => {
            const refreshed = await refreshCapability(generation);
            if (!refreshed && generation === lifecycleGeneration && analyticsConsentChoice() === 'accepted') {
                if (currentCapability()) scheduleRefresh(generation, true);
                else await ensureAcceptedLifecycle(generation, true);
            }
        });
    }, delay);
}

async function refreshCapability(generation: number) {
    const token = currentCapability();
    if (!token || generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return false;
    const response = await postStable(
        'handoff/',
        { capability: token },
        201,
        2,
        () => generation === lifecycleGeneration && analyticsConsentChoice() === 'accepted'
    );
    const parsed = validCapabilityResponse(response, 'handoff_token');
    if (!parsed || generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return false;
    const wasBound = captureState.boundCapability === token;
    const wasTrusted = trustedBoundCapability === token;
    const wasTouchReady = touchReadyCapability === token;
    captureState.capability = parsed.token;
    captureState.expiresAt = Date.now() + parsed.expiresIn * 1000;
    captureState.boundCapability = wasBound ? parsed.token : '';
    trustedBoundCapability = wasTrusted ? parsed.token : '';
    touchReadyCapability = wasTouchReady ? parsed.token : '';
    carryCapabilityOperationGuards(token, parsed.token);
    persistCaptureState();
    scheduleRefresh(generation);
    return true;
}

async function ensureAuthenticatedBind(generation: number) {
    const transport = authenticatedBindTransport;
    const token = currentCapability();
    if (
        !transport ||
        authenticatedSessionResolution !== 'authenticated' ||
        !captureState.serverGranted ||
        !token ||
        trustedBoundCapability === token ||
        generation !== lifecycleGeneration ||
        analyticsConsentChoice() !== 'accepted'
    )
        return 'skipped' as const;
    const sessionGeneration = authenticatedSessionGeneration;
    const key = `bind:${sessionGeneration}:${token}`;
    if (permanentOperations.has(key)) return 'failed' as const;
    const retry = retryOperations.get(key);
    if (retry && retry.nextAt > Date.now()) {
        scheduleLifecycleRetry(retry.nextAt - Date.now());
        return 'failed' as const;
    }
    let result: AcquisitionBindTransportResult;
    try {
        result = await transport(token);
    } catch {
        result = { status: 0 };
    }
    if (
        sessionGeneration !== authenticatedSessionGeneration ||
        authenticatedSessionResolution !== 'authenticated' ||
        generation !== lifecycleGeneration ||
        token !== captureState.capability
    )
        return 'failed' as const;
    const data = result.data as Record<string, unknown> | undefined;
    if (result.status === 202 && data?.bound === true && typeof data.created === 'boolean') {
        retryOperations.delete(key);
        permanentOperations.delete(key);
        captureState.boundCapability = token;
        trustedBoundCapability = token;
        bindRequiredBeforeTouch = false;
        persistCaptureState();
        return 'bound' as const;
    }
    if (classifyStatus(result.status) === 'retryable') {
        registerRetry(key, Math.max(0, result.retryAfterMs || 0));
    } else {
        retryOperations.delete(key);
        permanentOperations.add(key);
    }
    return result.status === 409 ? ('conflict' as const) : ('failed' as const);
}

export function configureAcquisitionAuthenticatedBindTransport(transport: AcquisitionBindTransport | null) {
    authenticatedBindTransport = transport;
    if (transport && authenticatedSessionResolution === 'authenticated' && started) {
        const generation = lifecycleGeneration;
        queueLifecycle(async () => {
            await ensureAuthenticatedBind(generation);
        });
    }
}

function normalizedSessionIdentity(value: string | number | undefined) {
    const identity = String(value ?? '');
    return /^[A-Za-z0-9:_-]{1,128}$/.test(identity) ? identity : '';
}

export function setAcquisitionAuthenticatedSession(
    resolution: AcquisitionSessionResolution,
    identityKey?: string | number
) {
    const nextIdentity = resolution === 'authenticated' ? normalizedSessionIdentity(identityKey) : '';
    const nextResolution = resolution === 'authenticated' && !nextIdentity ? 'pending' : resolution;
    if (authenticatedSessionResolution === nextResolution && authenticatedSessionIdentity === nextIdentity) {
        synchronizePurchaseIdentity();
        return;
    }
    const previousResolution = authenticatedSessionResolution;
    const previousIdentity = authenticatedSessionIdentity;
    authenticatedSessionResolution = nextResolution;
    authenticatedSessionIdentity = nextIdentity;
    authenticatedSessionGeneration += 1;
    purchaseIdentityGeneration += 1;
    purchaseIdentityRetryAttempt = 0;
    purchaseIdentityFingerprint = '';
    clearPurchaseIdentityRetry();
    resetOperationGuards('bind:');
    captureState = readCaptureState();
    const accountChanged =
        previousResolution === 'authenticated' &&
        nextResolution === 'authenticated' &&
        previousIdentity !== nextIdentity;
    const persistedBoundSubject = Boolean(
        captureState.capability &&
            captureState.boundCapability === captureState.capability &&
            trustedBoundCapability !== captureState.capability
    );
    if (
        accountChanged ||
        (nextResolution === 'anonymous' && (previousResolution === 'authenticated' || persistedBoundSubject))
    ) {
        trustedBoundCapability = '';
        bindRequiredBeforeTouch = false;
        rotateAcquisitionSubjectAfterLogout();
        synchronizePurchaseIdentity();
        return;
    }
    if (nextResolution !== 'authenticated') {
        synchronizePurchaseIdentity();
        return;
    }
    if (persistedBoundSubject) {
        trustedBoundCapability = '';
        bindRequiredBeforeTouch = true;
    }
    synchronizePurchaseIdentity();
    if (!started) return;
    const generation = lifecycleGeneration;
    queueLifecycle(async () => {
        await ensureAcceptedLifecycle(generation, true);
    });
}

async function ensureAcceptedLifecycle(generation: number, requestTouch: boolean) {
    if (!apiBase() || generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
    if (captureState.rotateAfterWithdraw) {
        if (captureState.capability && !(await recordConsent('withdraw', true))) return;
        captureState = emptyState();
        persistCaptureState();
        subjectAttemptGeneration = -1;
        if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
    }
    if (captureState.withdrawEventId) {
        if (!(await recordConsent('withdraw', true))) return;
        if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
    }
    if (!currentCapability()) {
        if (captureState.capability && (captureState.serverGranted || captureState.acceptEventId)) {
            if (!(await recordConsent('withdraw', true))) return;
            if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
        }
        subjectAttemptGeneration = -1;
        if (!(await createSubject(generation))) return;
        if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
    }
    if (!captureState.serverGranted && !(await recordConsent('accept', false, generation))) return;
    if (generation !== lifecycleGeneration || analyticsConsentChoice() !== 'accepted') return;
    scheduleRefresh(generation);
    if (bindRequiredBeforeTouch && authenticatedSessionResolution === 'pending') return;
    if (bindRequiredBeforeTouch) {
        const bindResult = await ensureAuthenticatedBind(generation);
        if (bindResult === 'conflict') {
            bindRequiredBeforeTouch = false;
            trustedBoundCapability = '';
            rotateAcquisitionSubjectAfterLogout();
            return;
        }
        if (bindResult !== 'bound') return;
        await attemptTouchBeforeObservations(generation, requestTouch);
        return;
    }
    await attemptTouchBeforeObservations(generation, requestTouch);
    await ensureAuthenticatedBind(generation);
}

async function ensureRejectedLifecycle(force = false) {
    clearRefreshTimer();
    if (!apiBase() || !captureState.capability) return;
    if (force || captureState.serverGranted || captureState.acceptEventId || captureState.withdrawEventId) {
        const withdrawn = await recordConsent('withdraw', true);
        if (withdrawn && captureState.rotateAfterWithdraw) {
            captureState = emptyState();
            persistCaptureState();
        }
    }
}

function queueLifecycle(task: () => Promise<void>) {
    lifecycleChain = lifecycleChain.then(task, task).catch(() => {});
    return lifecycleChain;
}

export function rotateAcquisitionSubjectAfterLogout() {
    captureState = readCaptureState();
    captureState.rotateAfterWithdraw = true;
    captureState.pendingTouch = null;
    captureState.lastTouchKey = '';
    captureState.pendingObservations = [];
    captureState.deliveredObservationKeys = [];
    persistCaptureState();
    lifecycleGeneration += 1;
    subjectAttemptGeneration = -1;
    trustedBoundCapability = '';
    bindRequiredBeforeTouch = false;
    touchReadyCapability = '';
    resetOperationGuards();
    if (!started) return;
    const generation = lifecycleGeneration;
    queueLifecycle(async () => {
        if (analyticsConsentChoice() === 'accepted') await ensureAcceptedLifecycle(generation, true);
        else await ensureRejectedLifecycle(true);
    });
}

function safeHitPath() {
    return window.location.pathname || '/';
}

function sendHit() {
    const path = safeHitPath();
    if (!vendorInitialized || analyticsConsentChoice() !== 'accepted' || path === lastHitPath) return;
    try {
        window.ym?.(COUNTER_ID, 'hit', path);
        lastHitPath = path;
    } catch {}
}

function initializeVendor(generation: number) {
    if (
        generation !== lifecycleGeneration ||
        vendorInitialized ||
        analyticsConsentChoice() !== 'accepted' ||
        typeof window.ym !== 'function'
    )
        return;
    try {
        window.ym(COUNTER_ID, 'init', {
            defer: true,
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
        });
        vendorInitialized = true;
        sendHit();
        if (registrationSuccessGoalPending && !registrationSuccessGoalDelivered) {
            window.ym(COUNTER_ID, 'reachGoal', REGISTRATION_SUCCESS_GOAL);
            registrationSuccessGoalPending = false;
            registrationSuccessGoalDelivered = true;
        }
        synchronizePurchaseIdentity();
    } catch {
        vendorInitialized = false;
    }
}

function loadVendor(generation: number) {
    if (analyticsConsentChoice() !== 'accepted') return;
    window.disableYaCounter112761520 = false;
    document.querySelectorAll('input, textarea').forEach((field) => field.classList.add('ym-disable-keys'));
    if (vendorLoaded) {
        initializeVendor(generation);
        return;
    }
    if (document.querySelector('script[data-lm-app-metrika][data-lm-state="loading"]')) return;
    if (typeof window.ym !== 'function') {
        const stub = function ym(...args: unknown[]) {
            (stub.a = stub.a || []).push(args);
        } as Ym;
        stub.a = [];
        stub.l = Date.now();
        stub.__lmStub = true;
        window.ym = stub;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://mc.yandex.ru/metrika/tag.js?id=${COUNTER_ID}`;
    script.dataset.lmAppMetrika = '';
    script.dataset.lmState = 'loading';
    script.addEventListener('load', () => {
        script.dataset.lmState = 'loaded';
        vendorLoaded = true;
        initializeVendor(generation);
    });
    script.addEventListener('error', () => {
        script.remove();
    });
    document.head.append(script);
}

function stopVendor() {
    clearRefreshTimer();
    document
        .querySelectorAll('script[data-lm-app-metrika][data-lm-state="loading"]')
        .forEach((script) => script.remove());
    if (vendorInitialized) {
        try {
            window.ym?.(COUNTER_ID, 'destruct');
        } catch {}
    }
    vendorInitialized = false;
    lastHitPath = '';
    window.disableYaCounter112761520 = true;
}

function acceptCurrentChoice(requestTouch = true) {
    lifecycleGeneration += 1;
    subjectAttemptGeneration = -1;
    resetOperationGuards();
    const generation = lifecycleGeneration;
    window.disableYaCounter112761520 = false;
    loadVendor(generation);
    queueLifecycle(() => ensureAcceptedLifecycle(generation, requestTouch));
}

function rejectCurrentChoice(force = false) {
    lifecycleGeneration += 1;
    resetOperationGuards();
    captureState = readCaptureState();
    captureState.pendingObservations = [];
    captureState.deliveredObservationKeys = [];
    touchReadyCapability = '';
    registrationSuccessGoalPending = false;
    persistCaptureState();
    stopVendor();
    queueLifecycle(() => ensureRejectedLifecycle(force));
}

function synchronizeConsent() {
    captureState = readCaptureState();
    const next = analyticsConsentChoice();
    if (next === observedConsent) {
        if (next === 'accepted') {
            const generation = lifecycleGeneration;
            queueLifecycle(() => ensureAcceptedLifecycle(generation, Boolean(captureState.pendingTouch)));
        } else if (
            captureState.capability &&
            (captureState.serverGranted || captureState.acceptEventId || captureState.withdrawEventId)
        ) {
            queueLifecycle(() => ensureRejectedLifecycle());
        }
        return;
    }
    observedConsent = next;
    if (next === 'accepted') acceptCurrentChoice(true);
    else rejectCurrentChoice(true);
}

export function startAcquisitionBrowserBridge() {
    if (started) return () => {};
    started = true;
    captureState = readCaptureState();
    if (
        captureState.capability &&
        captureState.boundCapability === captureState.capability &&
        trustedBoundCapability !== captureState.capability
    )
        bindRequiredBeforeTouch = true;
    observedConsent = migrateAnalyticsConsent();
    window.disableYaCounter112761520 = observedConsent !== 'accepted';
    if (observedConsent === 'accepted') acceptCurrentChoice(true);
    else rejectCurrentChoice(Boolean(captureState.capability));
    synchronizePurchaseIdentity();

    const unlisten = history.listen(() => {
        sendHit();
        if (analyticsConsentChoice() === 'accepted') {
            const generation = lifecycleGeneration;
            queueLifecycle(() => ensureAcceptedLifecycle(generation, true));
        }
    });
    const onFocus = () => {
        synchronizeConsent();
        synchronizePurchaseIdentity();
    };
    const onVisibility = () => {
        if (!document.hidden) synchronizeConsent();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    syncTimer = window.setInterval(() => {
        if (!document.hidden) synchronizeConsent();
    }, 5000);
    return () => {
        unlisten();
        window.removeEventListener('focus', onFocus);
        window.removeEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, onFocus);
        document.removeEventListener('visibilitychange', onVisibility);
        if (syncTimer) window.clearInterval(syncTimer);
        clearRefreshTimer();
        clearLifecycleRetryTimer();
        clearPurchaseIdentityRetry();
        syncTimer = 0;
        lifecycleGeneration += 1;
        stopVendor();
        started = false;
    };
}

export function AcquisitionBrowserBridge() {
    useEffect(() => startAcquisitionBrowserBridge(), []);
    return null;
}

export function resetAcquisitionBrowserBridgeForTests() {
    clearRefreshTimer();
    clearLifecycleRetryTimer();
    if (syncTimer) window.clearInterval(syncTimer);
    syncTimer = 0;
    vendorLoaded = false;
    vendorInitialized = false;
    lifecycleGeneration += 1;
    observedConsent = 'unknown';
    lastHitPath = '';
    started = false;
    subjectAttemptGeneration = -1;
    captureState = emptyState();
    lifecycleChain = Promise.resolve();
    authenticatedSessionResolution = 'pending';
    authenticatedSessionIdentity = '';
    authenticatedSessionGeneration += 1;
    authenticatedBindTransport = null;
    purchaseIdentityTransport = null;
    purchaseIdentityGeneration += 1;
    purchaseIdentityRetryAttempt = 0;
    purchaseIdentityFingerprint = '';
    purchaseIdentityChain = Promise.resolve();
    clearPurchaseIdentityRetry();
    trustedBoundCapability = '';
    bindRequiredBeforeTouch = false;
    touchReadyCapability = '';
    registrationSuccessGoalPending = false;
    registrationSuccessGoalDelivered = false;
    permanentOperations.clear();
    retryOperations.clear();
}

captureAcquisitionHandoffFromFragment();
